/**
 * CampaignEvidence.js
 *
 * WHAT: Campaign closure, evidence package generation, and evidence viewing components.
 * WHY:  After all attestations are complete, compliance requires a formal evidence package
 *       (pre/post attestation reports, decision summaries) for audit. This file provides
 *       the modal to close a campaign (transitioning it to COMPLETED), a viewer to browse
 *       generated evidence artifacts, and retention settings for evidence lifecycle management.
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: Modal that checks closure readiness and transitions a campaign from ATTESTATION to COMPLETED.
 * WHY:  Closing a campaign is an irreversible action that locks all attestation decisions and
 *       triggers evidence generation. This modal validates that all in-scope accounts have
 *       decisions before allowing closure, preventing premature finalization.
 */
export const CloseCampaignModal = ({ campaign, show, onClose, onClosed }) => {
  const [readiness, setReadiness] = useState(null);
  const [closureNotes, setClosureNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Check closure readiness when modal opens
  useEffect(() => {
    if (show && campaign?.campaign_id) {
      checkReadiness();
    }
  }, [show, campaign]);

  const checkReadiness = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/closure-readiness/`)
      );
      const data = await response.json();
      if (data.success) {
        setReadiness(data);
      } else {
        setError(data.error || 'Failed to check closure readiness');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/close/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: campaign.campaign_id,
            closure_notes: closureNotes,
            closed_by: 'DAMA_ADMIN' // TODO: Get from auth context
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        setResult(data);
        if (onClosed) {
          onClosed(data);
        }
      } else {
        setError(data.error || 'Failed to close campaign');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setClosing(false);
    }
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: result ? '#28a745' : '#6c757d',
          color: '#fff',
          padding: '16px 20px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h5 style={{ margin: 0, fontWeight: 600 }}>
            <i className={`fas ${result ? 'fa-check-circle' : 'fa-archive'} me-2`}></i>
            {result ? 'Campaign Closed Successfully' : 'Close Campaign & Generate Evidence'}
          </h5>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-3">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Checking closure readiness...</p>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div>
              <div className="alert alert-success mb-3">
                <i className="fas fa-check-circle me-2"></i>
                <strong>Campaign has been closed and evidence package has been generated.</strong>
              </div>

              <h6 className="fw-bold mb-3">
                <i className="fas fa-file-alt me-2"></i>
                Generated Evidence Reports
              </h6>

              <table className="table table-sm table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Report Type</th>
                    <th>Report Name</th>
                    <th>Records</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.reports_generated?.map((report, idx) => (
                    <tr key={idx}>
                      <td><code>{report.report_type}</code></td>
                      <td>{report.report_name}</td>
                      <td>{report.record_count ?? '-'}</td>
                      <td>
                        {report.error ? (
                          <span className="text-danger">
                            <i className="fas fa-times me-1"></i>Error
                          </span>
                        ) : (
                          <span className="text-success">
                            <i className="fas fa-check me-1"></i>Generated
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="text-muted small mt-3">
                <i className="fas fa-info-circle me-1"></i>
                Evidence reports are now stored in the database and can be viewed from the Evidence Package viewer.
              </p>
            </div>
          )}

          {/* Readiness Check */}
          {!loading && !result && readiness && (
            <div>
              {/* Campaign Info */}
              <div className="mb-4">
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-flag me-2"></i>
                  Campaign: {campaign.campaign_name}
                </h6>
                <span className={`badge ${readiness.status === 'ATTESTATION' ? 'bg-warning' : 'bg-secondary'}`}>
                  {readiness.status}
                </span>
              </div>

              {/* Readiness Status */}
              <div className={`alert ${readiness.ready ? 'alert-success' : 'alert-warning'} mb-4`}>
                <div className="d-flex align-items-center">
                  <i className={`fas ${readiness.ready ? 'fa-check-circle' : 'fa-exclamation-triangle'} fa-2x me-3`}></i>
                  <div>
                    <h6 className="mb-1 fw-bold">
                      {readiness.ready ? 'Ready to Close' : 'Not Ready to Close'}
                    </h6>
                    <p className="mb-0 small">
                      {readiness.ready
                        ? 'All attestations are complete. Campaign can be closed.'
                        : readiness.reason || `${readiness.completed_attestations}/${readiness.total_users} attestations completed`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Attestation Progress */}
              {readiness.total_users > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Attestation Progress</h6>
                  <div className="progress" style={{ height: '25px' }}>
                    <div
                      className={`progress-bar ${readiness.ready ? 'bg-success' : 'bg-warning'}`}
                      role="progressbar"
                      style={{ width: `${readiness.completion_percentage}%` }}
                    >
                      {readiness.completion_percentage}%
                    </div>
                  </div>
                  <small className="text-muted">
                    {readiness.completed_attestations} of {readiness.total_users} attestations completed
                  </small>
                </div>
              )}

              {/* Retention Info */}
              <div className="mb-4">
                <div className="alert alert-info mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Evidence will be retained for <strong>{readiness.retention_years} years</strong> per retention policy.
                </div>
              </div>

              {/* Closure Notes */}
              {readiness.ready && (
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="fas fa-sticky-note me-2"></i>
                    Closure Notes (Optional)
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={closureNotes}
                    onChange={(e) => setClosureNotes(e.target.value)}
                    placeholder="Enter any notes about this campaign closure..."
                  />
                </div>
              )}

              {/* What Happens on Close */}
              <div className="card bg-light mb-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-info-circle me-2"></i>
                    What happens when you close this campaign?
                  </h6>
                  <ul className="mb-0 small">
                    <li>Campaign status changes to <strong>COMPLETED</strong></li>
                    <li>All attestation data becomes <strong>read-only</strong></li>
                    <li>Evidence package with <strong>11 reports</strong> is generated (JSON + PDF)</li>
                    <li>Reports are stored in the database with integrity hashes</li>
                    <li>Evidence is retained for <strong>{readiness.retention_years} years</strong></li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px'
        }}>
          {result ? (
            <button
              type="button"
              className="btn btn-success"
              onClick={onClose}
            >
              <i className="fas fa-check me-2"></i>
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleClose}
                disabled={!readiness?.ready || closing}
              >
                {closing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Closing Campaign...
                  </>
                ) : (
                  <>
                    <i className="fas fa-archive me-2"></i>
                    Close Campaign & Generate Evidence
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};


/**
 * WHAT: Modal that lists and displays generated evidence artifacts for a completed campaign.
 * WHY:  Auditors need to access pre- and post-attestation reports, decision summaries, and
 *       other evidence without navigating to external storage. This viewer fetches the
 *       evidence catalog and renders individual reports inline.
 */
export const EvidenceViewer = ({ campaign, show, onClose }) => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [includeJson, setIncludeJson] = useState(false);

  // Load evidence list when modal opens
  useEffect(() => {
    if (show && campaign?.campaign_id) {
      loadEvidence();
      setGenerateResult(null);
      setSelectedIds([]);
    }
  }, [show, campaign]);

  const loadEvidence = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/evidence/`)
      );
      const data = await response.json();
      if (data.success) {
        setEvidence(data.reports || []);
      } else {
        setError(data.error || 'Failed to load evidence');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const generateEvidence = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/generate-evidence/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ generated_by: 'DAMA_ADMIN' })
        }
      );
      const data = await response.json();
      if (data.success) {
        setGenerateResult(data);
        // Reload evidence list
        loadEvidence();
      } else {
        setError(data.error || 'Failed to generate evidence');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setGenerating(false);
    }
  };

  const loadReportData = async (report) => {
    setSelectedReport(report);
    setLoadingReport(true);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`evidence/${report.id}/`)
      );
      const data = await response.json();
      if (data.success) {
        setReportData(data.report);
      } else {
        setError(data.error || 'Failed to load report');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadPdf = (report) => {
    // Open the PDF download endpoint in a new tab
    window.open(
      API_CONFIG.getDatabasesEndpoint(`evidence/${report.id}/download/`),
      '_blank'
    );
  };

  // Multi-select helpers
  const toggleSelectAll = () => {
    if (selectedIds.length === evidence.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(evidence.map(e => e.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const exportSelected = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/export-evidence/`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            evidence_ids: selectedIds.length > 0 ? selectedIds : [],
            include_json: includeJson
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${campaign.campaign_name.replace(/\s+/g, '_')}_Evidence_Package.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || 'Failed to export evidence package');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: '#17a2b8',
          color: '#fff',
          padding: '16px 20px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <h5 style={{ margin: 0, fontWeight: 600 }}>
            <i className="fas fa-folder-open me-2"></i>
            Evidence Package: {campaign?.campaign_name}
          </h5>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflow: 'auto',
          flex: 1
        }}>
          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-3">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading evidence package...</p>
            </div>
          )}

          {/* Evidence List */}
          {!loading && evidence.length === 0 && !generateResult && (
            <div className="text-center py-4">
              <div className="alert alert-info mb-4">
                <i className="fas fa-info-circle me-2"></i>
                No evidence reports found for this campaign.
              </div>
              <button
                className="btn btn-primary"
                onClick={generateEvidence}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Generating Evidence...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-alt me-2"></i>
                    Generate Evidence Package
                  </>
                )}
              </button>
            </div>
          )}

          {/* Generation Result */}
          {generateResult && (
            <div className="alert alert-success mb-3">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Evidence package generated successfully!</strong>
              <span className="ms-2">({generateResult.reports_generated?.length} reports created)</span>
            </div>
          )}

          {!loading && evidence.length > 0 && !selectedReport && (
            <div>
              {/* Export Toolbar */}
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-muted">
                    {selectedIds.length > 0 ? (
                      <><strong>{selectedIds.length}</strong> of {evidence.length} selected</>
                    ) : (
                      <>Select reports to export as a package</>
                    )}
                  </span>
                  <div className="form-check mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="includeJson"
                      checked={includeJson}
                      onChange={(e) => setIncludeJson(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="includeJson">
                      Include JSON files
                    </label>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={toggleSelectAll}
                  >
                    {selectedIds.length === evidence.length ? (
                      <><i className="fas fa-square me-1"></i>Deselect All</>
                    ) : (
                      <><i className="fas fa-check-square me-1"></i>Select All</>
                    )}
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={exportSelected}
                    disabled={exporting}
                    title={selectedIds.length === 0 ? 'Export all reports' : `Export ${selectedIds.length} selected reports`}
                  >
                    {exporting ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Exporting...</>
                    ) : (
                      <><i className="fas fa-file-archive me-1"></i>
                        {selectedIds.length === 0 ? 'Export All' : `Export Selected (${selectedIds.length})`}
                      </>
                    )}
                  </button>
                </div>
              </div>

              <table className="table table-hover">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.length === evidence.length && evidence.length > 0}
                        onChange={toggleSelectAll}
                        title="Select All"
                      />
                    </th>
                    <th>Report Name</th>
                    <th>Type</th>
                    <th>Records</th>
                    <th>Generated</th>
                    <th>Integrity</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {evidence.map((report) => (
                    <tr
                      key={report.id}
                      className={selectedIds.includes(report.id) ? 'table-active' : ''}
                    >
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(report.id)}
                          onChange={() => toggleSelect(report.id)}
                        />
                      </td>
                      <td>
                        <strong>{report.report_name}</strong>
                      </td>
                      <td><code className="small">{report.report_type}</code></td>
                      <td>{report.record_count ?? '-'}</td>
                      <td>
                        <small>{formatDate(report.generated_date)}</small>
                        <br/>
                        <small className="text-muted">by {report.generated_by}</small>
                      </td>
                      <td>
                        <span className="text-success small">
                          <i className="fas fa-shield-alt me-1"></i>
                          Verified
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => loadReportData(report)}
                          title="View JSON Report"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {report.has_pdf && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => downloadPdf(report)}
                            title="Download PDF"
                          >
                            <i className="fas fa-file-pdf"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="alert alert-light border mt-3">
                <div className="d-flex align-items-center">
                  <i className="fas fa-clock text-muted me-3 fa-2x"></i>
                  <div>
                    <strong>Retention Policy</strong>
                    <p className="mb-0 small text-muted">
                      Evidence will be retained until: <strong>{evidence[0]?.retention_until || 'N/A'}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Report Detail View */}
          {selectedReport && (
            <div>
              <button
                className="btn btn-link text-decoration-none mb-3 p-0"
                onClick={() => {
                  setSelectedReport(null);
                  setReportData(null);
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Evidence List
              </button>

              <div className="card">
                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="fas fa-file-alt me-2"></i>
                    {selectedReport.report_name}
                  </h6>
                  {selectedReport.has_pdf && (
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => downloadPdf(selectedReport)}
                    >
                      <i className="fas fa-download me-1"></i>
                      Download PDF
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {loadingReport ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : reportData ? (
                    <div>
                      {/* Report Metadata */}
                      <div className="mb-3 p-3 bg-light rounded">
                        <div className="row small">
                          <div className="col-md-3">
                            <strong>Type:</strong> {reportData.report_type}
                          </div>
                          <div className="col-md-3">
                            <strong>Records:</strong> {reportData.record_count ?? '-'}
                          </div>
                          <div className="col-md-3">
                            <strong>Generated:</strong> {formatDate(reportData.generated_date)}
                          </div>
                          <div className="col-md-3">
                            <strong>Hash:</strong>
                            <code className="ms-1" style={{ fontSize: '0.7rem' }}>
                              {reportData.json_hash?.substring(0, 16)}...
                            </code>
                          </div>
                        </div>
                      </div>

                      {/* Report JSON Content */}
                      <div style={{
                        maxHeight: '400px',
                        overflow: 'auto',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '15px'
                      }}>
                        <pre style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.85rem'
                        }}>
                          {JSON.stringify(reportData.report_json, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-warning mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Unable to load report data.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};


/**
 * WHAT: Admin settings panel for configuring evidence retention periods and triggering archival.
 * WHY:  Regulatory requirements mandate keeping attestation evidence for a defined period
 *       (e.g., 7 years for SOX). This component lets admins set retention duration and
 *       manually trigger cleanup of expired evidence packages.
 */
export const RetentionSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [retentionStatus, setRetentionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Form state
  const [retentionYears, setRetentionYears] = useState(7);
  const [retentionAction, setRetentionAction] = useState('ARCHIVE');
  const [autoPurge, setAutoPurge] = useState(false);

  useEffect(() => {
    loadSettings();
    loadRetentionStatus();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('settings/retention/')
      );
      const data = await response.json();
      if (data.success) {
        setRetentionYears(data.settings.evidence_retention_years || 7);
        setRetentionAction(data.settings.evidence_retention_action || 'ARCHIVE');
        setAutoPurge(data.settings.evidence_auto_purge_enabled || false);
      } else {
        setError(data.error || 'Failed to load settings');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionStatus = async () => {
    setLoadingStatus(true);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('retention/status/')
      );
      const data = await response.json();
      if (data.success) {
        setRetentionStatus(data);
      }
    } catch (err) {
      console.error('Failed to load retention status:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('settings/retention/update/'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            evidence_retention_years: retentionYears,
            evidence_retention_action: retentionAction,
            evidence_auto_purge_enabled: autoPurge,
            modified_by: 'DAMA_ADMIN'
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Settings saved successfully');
        loadSettings();
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveExpired = async () => {
    if (!window.confirm(`Are you sure you want to ${retentionAction === 'DELETE' ? 'permanently DELETE' : 'archive'} all expired campaign data? This action cannot be undone.`)) {
      return;
    }

    setArchiving(true);
    setError(null);
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('retention/archive/'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: retentionAction,
            archived_by: 'DAMA_ADMIN'
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess(`Successfully processed ${data.processed} campaigns`);
        loadRetentionStatus();
      } else {
        setError(data.error || 'Failed to process campaigns');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="fas fa-archive me-2"></i>
          Campaign Data Retention
        </h4>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      {/* Data Covered Section */}
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">
            <i className="fas fa-database me-2"></i>
            Data Covered by Retention Policy
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            The retention policy applies to all completed campaign data, including:
          </p>
          <div className="row">
            <div className="col-md-6">
              <div className="card bg-light mb-3">
                <div className="card-body">
                  <h6 className="fw-bold text-primary">
                    <i className="fas fa-file-alt me-2"></i>
                    Evidence Package
                  </h6>
                  <ul className="mb-0 small">
                    <li>Campaign Summary Report (JSON + PDF)</li>
                    <li>Executive Summary Report</li>
                    <li>Full Attestation Report</li>
                    <li>Approved/Revoked Access Reports</li>
                    <li>Pre-Attestation Baseline</li>
                    <li>Attester Activity Log</li>
                    <li>Delegation History</li>
                    <li>Risk Analysis Summary</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card bg-light mb-3">
                <div className="card-body">
                  <h6 className="fw-bold text-success">
                    <i className="fas fa-users me-2"></i>
                    UAR Campaign Data
                  </h6>
                  <ul className="mb-0 small">
                    <li>User access records (database permissions)</li>
                    <li>Privilege levels and mappings</li>
                    <li>Attestation decisions (Retained/Removed/Modified)</li>
                    <li>Attester assignments</li>
                    <li>Comments and justifications</li>
                    <li>Risk scores and calculations</li>
                    <li>Campaign report table (uar_report_*)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Retention Status */}
      <div className="card mb-4">
        <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-chart-pie me-2"></i>
            Retention Status
          </h5>
          <button
            className="btn btn-sm btn-light"
            onClick={loadRetentionStatus}
            disabled={loadingStatus}
          >
            <i className={`fas fa-sync ${loadingStatus ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
        <div className="card-body">
          {loadingStatus ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary"></div>
              <span className="ms-2">Loading status...</span>
            </div>
          ) : retentionStatus ? (
            <div>
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card border-success">
                    <div className="card-body text-center">
                      <h2 className="text-success mb-0">{retentionStatus.summary?.active || 0}</h2>
                      <small className="text-muted">Active</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-warning">
                    <div className="card-body text-center">
                      <h2 className="text-warning mb-0">{retentionStatus.summary?.expiring_soon || 0}</h2>
                      <small className="text-muted">Expiring Soon (90 days)</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-danger">
                    <div className="card-body text-center">
                      <h2 className="text-danger mb-0">{retentionStatus.summary?.expired || 0}</h2>
                      <small className="text-muted">Expired</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-secondary">
                    <div className="card-body text-center">
                      <h2 className="text-secondary mb-0">{retentionStatus.summary?.no_retention || 0}</h2>
                      <small className="text-muted">No Retention Set</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expired Campaigns Alert */}
              {retentionStatus.summary?.expired > 0 && (
                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>{retentionStatus.summary.expired} campaign(s)</strong> have exceeded their retention period.
                  </div>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleArchiveExpired}
                    disabled={archiving}
                  >
                    {archiving ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span> Processing...</>
                    ) : (
                      <><i className="fas fa-archive me-1"></i> {retentionAction === 'DELETE' ? 'Delete' : 'Archive'} Expired</>
                    )}
                  </button>
                </div>
              )}

              {/* Campaigns Table */}
              {retentionStatus.campaigns?.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th>Campaign</th>
                        <th>Completed</th>
                        <th>Retention Until</th>
                        <th>Days Left</th>
                        <th>Status</th>
                        <th>Evidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retentionStatus.campaigns.map(c => (
                        <tr key={c.campaign_id}>
                          <td><strong>{c.campaign_name}</strong></td>
                          <td>{c.completed_date ? new Date(c.completed_date).toLocaleDateString() : '-'}</td>
                          <td>{c.retention_until || '-'}</td>
                          <td>
                            {c.days_until_expiry !== null ? (
                              <span className={c.days_until_expiry < 0 ? 'text-danger' : c.days_until_expiry < 90 ? 'text-warning' : 'text-success'}>
                                {c.days_until_expiry < 0 ? `${Math.abs(c.days_until_expiry)} days overdue` : `${c.days_until_expiry} days`}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            <span className={`badge ${
                              c.retention_status === 'EXPIRED' ? 'bg-danger' :
                              c.retention_status === 'EXPIRING_SOON' ? 'bg-warning' :
                              c.retention_status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'
                            }`}>
                              {c.retention_status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            {c.evidence_generated ? (
                              <span className="text-success"><i className="fas fa-check"></i> {c.evidence_count} reports</span>
                            ) : (
                              <span className="text-muted"><i className="fas fa-times"></i> None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted mb-0">No completed campaigns found.</p>
          )}
        </div>
      </div>

      {/* Settings Card */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-cog me-2"></i>
            Retention Policy Settings
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Retention Period (Years)
                </label>
                <select
                  className="form-select"
                  value={retentionYears}
                  onChange={(e) => setRetentionYears(parseInt(e.target.value))}
                >
                  {[1, 2, 3, 5, 7, 10, 15, 20, 25].map(year => (
                    <option key={year} value={year}>{year} year{year !== 1 ? 's' : ''}</option>
                  ))}
                </select>
                <small className="text-muted">
                  How long to retain all campaign data after closure
                </small>
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Action on Expiry
                </label>
                <select
                  className="form-select"
                  value={retentionAction}
                  onChange={(e) => setRetentionAction(e.target.value)}
                >
                  <option value="ARCHIVE">Archive (Mark as archived but keep all data)</option>
                  <option value="DELETE">Delete (Permanently remove all campaign data)</option>
                </select>
                <small className="text-muted">
                  What to do when campaign reaches retention limit
                </small>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="autoPurge"
                checked={autoPurge}
                onChange={(e) => setAutoPurge(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="autoPurge">
                <strong>Enable Automatic Processing</strong>
                <br/>
                <small className="text-muted">
                  Automatically process expired campaigns based on the action above (runs daily)
                </small>
              </label>
            </div>
          </div>

          <div className="alert alert-warning mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> If "Delete" is selected, expired campaign data including all
            user access records, attestation decisions, and evidence reports will be <strong>permanently removed</strong>.
          </div>

          <div className="alert alert-info mb-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Compliance Note:</strong> Most regulatory frameworks (SOX, GDPR, HIPAA) require
            audit evidence to be retained for 5-7 years. Consult your compliance team before
            reducing the retention period.
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default { CloseCampaignModal, EvidenceViewer, RetentionSettings };
